/**
 * Device ID Utility
 * 
 * Generates and persists a consistent device-level guest ID using localStorage.
 * This ensures that a guest user gets the same ID across multiple sessions
 * on the same device, enabling:
 *   1. Consistent push notification targeting (FCM token mapped to device ID)
 *   2. Re-identification when rejoining invite conversations
 *   3. Server-side message routing to the correct device
 */

const DEVICE_ID_KEY = 'd2c_device_id';

/**
 * Generates a random device ID.
 * Format: "device_" + timestamp (base36) + random string
 * @returns {string}
 */
function generateDeviceId() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `device_${timestamp}${random}`;
}

/**
 * Gets or creates a persistent device ID.
 * The ID is stored in localStorage and remains consistent
 * across sessions on the same device/browser.
 * 
 * @returns {string} The device ID
 */
export function getDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  
  if (!deviceId) {
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log('📱 Generated new device ID:', deviceId);
  }
  
  return deviceId;
}

/**
 * Checks if a device ID already exists.
 * @returns {boolean}
 */
export function hasDeviceId() {
  return localStorage.getItem(DEVICE_ID_KEY) !== null;
}

/**
 * Clears the stored device ID (for testing/reset).
 */
export function clearDeviceId() {
  localStorage.removeItem(DEVICE_ID_KEY);
}
